import type { TreeFolderNode, TreeNode, WorkspaceFileEntry } from './types';

/**
 * Build a nested folder/file tree from a FLAT list of canonical file entries.
 *
 * Rules:
 * - Paths are split on `/`. Leading/trailing slashes are ignored.
 * - Intermediate segments become folders (created on demand, deduped).
 * - Sorting at every level: folders first, then files; each group sorted
 *   case-insensitively by name (stable, locale-aware).
 */
export function buildFileTree(files: WorkspaceFileEntry[]): TreeNode[] {
  const root: TreeFolderNode = { type: 'folder', name: '', path: '', children: [] };
  // path -> folder node, so we create each folder exactly once.
  const folders = new Map<string, TreeFolderNode>();
  folders.set('', root);

  const ensureFolder = (segments: string[]): TreeFolderNode => {
    let current = root;
    let accum = '';
    for (const seg of segments) {
      accum = accum ? `${accum}/${seg}` : seg;
      let next = folders.get(accum);
      if (!next) {
        next = { type: 'folder', name: seg, path: accum, children: [] };
        folders.set(accum, next);
        current.children.push(next);
      }
      current = next;
    }
    return current;
  };

  for (const file of files) {
    const clean = file.path.replace(/^\/+|\/+$/g, '');
    if (!clean) continue;
    const segments = clean.split('/');
    const name = segments.pop() as string;
    const parent = ensureFolder(segments);
    // Guard against a folder + file colliding on the same path.
    if (parent.children.some((c) => c.type === 'file' && c.name === name)) continue;
    parent.children.push({ type: 'file', name, path: clean, file });
  }

  sortNodes(root.children);
  return root.children;
}

function sortNodes(nodes: TreeNode[]): void {
  nodes.sort((a, b) => {
    if (a.type !== b.type) return a.type === 'folder' ? -1 : 1;
    return a.name.localeCompare(b.name, undefined, { sensitivity: 'accent' });
  });
  for (const node of nodes) {
    if (node.type === 'folder') sortNodes(node.children);
  }
}

/**
 * Filter a built tree by a case-insensitive substring against the full path.
 * A folder is kept when any descendant file matches. Returns a new tree; the
 * input is not mutated.
 */
export function filterTree(nodes: TreeNode[], query: string): TreeNode[] {
  const q = query.trim().toLowerCase();
  if (!q) return nodes;

  const walk = (list: TreeNode[]): TreeNode[] => {
    const out: TreeNode[] = [];
    for (const node of list) {
      if (node.type === 'file') {
        if (node.path.toLowerCase().includes(q) || node.name.toLowerCase().includes(q)) {
          out.push(node);
        }
      } else {
        const children = walk(node.children);
        if (children.length > 0 || node.name.toLowerCase().includes(q)) {
          out.push({ ...node, children });
        }
      }
    }
    return out;
  };

  return walk(nodes);
}

/** Every folder path in the tree — used to expand-all when a filter is active. */
export function collectFolderPaths(nodes: TreeNode[]): string[] {
  const paths: string[] = [];
  const walk = (list: TreeNode[]) => {
    for (const node of list) {
      if (node.type === 'folder') {
        paths.push(node.path);
        walk(node.children);
      }
    }
  };
  walk(nodes);
  return paths;
}

/** Total byte size across a flat file list. */
export function totalSize(files: WorkspaceFileEntry[]): number {
  return files.reduce((sum, f) => sum + (Number.isFinite(f.size) ? f.size : 0), 0);
}

/** Human-readable byte size. */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(value >= 10 || Number.isInteger(value) ? 0 : 1)} ${units[unit]}`;
}

/** Format an mtime/createdAt that may arrive as epoch ms, epoch s, or ISO. */
export function formatTimestamp(value: string | number | undefined): string {
  if (value === undefined || value === null || value === '') return '—';
  let date: Date;
  if (typeof value === 'number') {
    // Heuristic: seconds vs ms.
    date = new Date(value < 1e12 ? value * 1000 : value);
  } else {
    const asNum = Number(value);
    date = Number.isFinite(asNum) && /^\d+$/.test(value.trim())
      ? new Date(asNum < 1e12 ? asNum * 1000 : asNum)
      : new Date(value);
  }
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString();
}
